import { Link } from "react-router-dom";
import { Landmark, ShieldCheck, FileLock2 } from "lucide-react";

/**
 * Shared chrome for the Bank Sponsor Demo Portal.
 *
 * - Top bar: verqo logo (existing brand) + "Sponsor Bank Review · Sandbox" pill.
 * - Children rendered in the main column.
 * - Footer with "All flows are sandboxed — no real KYB, payments, or SMS fired" notice.
 */
export default function BankDemoShell({ children, crumbs = [] }) {
  return (
    <div className="min-h-screen bg-[#0b0d12] text-white">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0b0d12]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/bank-demo" className="flex items-center gap-2.5" data-testid="bank-demo-nav-logo">
            <div className="w-7 h-7 rounded-md bg-white grid place-items-center">
              <span className="text-[#0b0d12] font-bold text-[13px]">v</span>
            </div>
            <span className="font-semibold text-[16px] tracking-tight">verqo</span>
            <span className="hidden sm:inline-flex items-center gap-1.5 ml-3 px-2.5 py-1 rounded-full text-[11px] font-medium bg-amber-500/15 text-amber-300 border border-amber-500/25">
              <ShieldCheck className="w-3 h-3" />
              Sponsor Bank Review · Sandbox
            </span>
          </Link>
          <div className="flex items-center gap-3 text-[13px] text-white/65">
            {crumbs.length > 0 && (
              <nav className="hidden md:flex items-center gap-2" data-testid="bank-demo-crumbs">
                {crumbs.map((c, i) => (
                  <span key={i} className="flex items-center gap-2">
                    {i > 0 && <span className="text-white/30">/</span>}
                    {c.to ? (
                      <Link to={c.to} className="hover:text-white transition-colors">{c.label}</Link>
                    ) : (
                      <span className="text-white">{c.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            )}
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-1.5 text-white/60 hover:text-white transition-colors"
              data-testid="bank-demo-back-to-marketing"
            >
              ← Marketing site
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">{children}</main>

      <footer className="mt-12 border-t border-white/10 bg-[#070910]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[12px] text-white/55">
          <div className="flex items-center gap-2">
            <FileLock2 className="w-3.5 h-3.5" />
            <span>This portal is a sandboxed demo. No real KYB, ACH, or SMS calls are fired during the walkthrough.</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/legal/aml-kyc" className="hover:text-white" data-testid="bank-demo-aml-link">AML/KYC policy</Link>
            <Link to="/legal/privacy" className="hover:text-white" data-testid="bank-demo-privacy-link">Privacy</Link>
            <Link to="/architecture" className="hover:text-white" data-testid="bank-demo-architecture-link">System architecture</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Small re-usable header to flag the bank-sponsor demo posture inside content sections.
export function BankSponsorPill() {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
      <Landmark className="w-3 h-3" />
      Built for sponsor banks
    </span>
  );
}
